import { graphql, useStaticQuery } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import React, { type FC } from 'react';

import { Html } from '@/components/content/html';
import { Logo } from '@/components/icons/logo';
import { ParamLink } from '@/components/links/param-link';
import { site } from '@/utils/site';

interface FooterQuery {
    footer: GraphqlSelect.Footer<'partnersTitle' | 'legalCopy'>;
}

const year = new Date().getFullYear();

export const Footer: FC = () => {
    const { footer } = useStaticQuery<FooterQuery>(graphql`
        query Footer {
            footer {
                partnersTitle

                legalCopy {
                    html
                }
            }
        }
    `);

    return (
        <footer className="container mt-120">
            <strong className="fs-20 c-secondary d-block text-center">{footer.partnersTitle}</strong>

            <div className="d-flex flex-wrap jc-center ai-center g-24 mt-32">
                <a
                    style={{ border: 'none' }}
                    className="box-logotype"
                    target="_blank"
                    href="https://www.aesan.gob.es/AECOSAN/web/home/aecosan_inicio.htm"
                    rel="noreferrer noopener"
                >
                    <StaticImage
                        width={100}
                        height={40}
                        src="../../../static/assets/footer/aecosan.png"
                        alt="Agencia Española de Seguridad Alimentaria y Nutrición"
                    />
                </a>

                <a
                    style={{ border: 'none' }}
                    className="box-logotype"
                    target="_blank"
                    href="https://www.enisa.europa.eu/"
                    rel="noreferrer noopener"
                >
                    <StaticImage
                        width={100}
                        height={82}
                        src="../../../static/assets/footer/enisa.png"
                        alt="ENISA, agencia de ciberseguridad de la Unión Europea"
                    />
                </a>

                <a
                    style={{ border: 'none' }}
                    className="box-logotype"
                    target="_blank"
                    href="https://www.asociacionfintech.es/"
                    rel="noreferrer noopener"
                >
                    <StaticImage
                        width={100}
                        height={64}
                        src="../../../static/assets/footer/fintech-insurtech.png"
                        alt="Asociación Española de Fintech e Insurtech"
                    />
                </a>
            </div>

            <div className="box bg-dark br-48 mt-32 py-80 container">
                <div className="d-flex jc-center">
                    <Logo theme="secondary" />
                </div>

                <nav className="d-flex flex-wrap g-32 container max-md mt-72">
                    <div style={{ flex: '1 0 200px' }} className="c-white">
                        <strong>PRESTALO</strong>

                        <ul className="list unstyled-list g-13 mt-24">
                            <li>
                                <ParamLink to="/prestamo-reunificacion-deudas/" className="move-x d-block">
                                    Reunificación de deudas
                                </ParamLink>
                            </li>

                            <li>
                                <ParamLink to="/prestamos/" className="move-x d-block">
                                    Préstamos
                                </ParamLink>
                            </li>

                            <li>
                                <a
                                    href="https://mejorescuentas.com/"
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    className="move-x d-block"
                                >
                                    Cuentas Bancarias by mejorescuentas
                                </a>
                            </li>

                            <li>
                                <ParamLink to="/quienes-somos/" className="move-x d-block">
                                    Quiénes Somos
                                </ParamLink>
                            </li>

                            <li>
                                <ParamLink to="/contacto/" className="move-x d-block">
                                    Contacto
                                </ParamLink>
                            </li>
                        </ul>
                    </div>

                    <div style={{ flex: '1 0 200px' }} className="c-white">
                        <strong>LEGAL</strong>

                        <ul className="list unstyled-list g-13 mt-24">
                            <li>
                                <ParamLink to="/politica-de-cookies/" className="move-x d-block">
                                    Política de Cookies
                                </ParamLink>
                            </li>

                            <li>
                                <ParamLink to="/condiciones-generales/" className="move-x d-block">
                                    Condiciones Generales
                                </ParamLink>
                            </li>

                            <li>
                                <ParamLink to="/politica-de-privacidad/" className="move-x d-block">
                                    Política de Privacidad
                                </ParamLink>
                            </li>

                            <li>
                                <ParamLink to="/aviso-legal/" className="move-x d-block">
                                    Aviso Legal
                                </ParamLink>
                            </li>
                        </ul>
                    </div>

                    <div style={{ flex: '1 0 200px' }} className="c-white">
                        <strong>DATOS DE CONTACTO</strong>

                        <ul className="list unstyled-list g-13 mt-24">
                            <li>
                                <a
                                    className="d-flex ai-center g-8"
                                    href={`tel:${site.contact.phone.replace(/\s/gm, '')}`}
                                >
                                    <img loading="lazy" width="27" height="28" src="/assets/footer/phone.svg" alt="" />
                                    {site.contact.phone}
                                </a>
                            </li>

                            <li>
                                <a
                                    className="d-flex ai-center g-8"
                                    target="_blank"
                                    href="https://wa.me/34689479006"
                                    rel="noopener noreferrer nofollow"
                                >
                                    <img
                                        loading="lazy"
                                        width="27"
                                        height="28"
                                        src="/assets/footer/whats-app.svg"
                                        alt=""
                                    />
                                    WhatsApp
                                </a>
                            </li>

                            <li>
                                <a className="d-flex ai-center g-8" href={`mailto:${site.contact.email}`}>
                                    <img loading="lazy" width="27" height="27" src="/assets/footer/email.svg" alt="" />
                                    {site.contact.email}
                                </a>
                            </li>

                            <li className="d-flex ai-start g-8">
                                <img loading="lazy" width="27" height="27" src="/assets/footer/workhours.svg" alt="" />

                                <span>
                                    Lunes a Viernes
                                    <br />
                                    09:00 a 17:00
                                </span>
                            </li>
                        </ul>
                    </div>
                </nav>

                <ul className="unstyled-list d-flex flex-wrap jc-center g-32 mt-72">
                    <li>
                        <a target="_blank" href="https://www.facebook.com/prestalo.oficial" rel="noopener noreferrer">
                            <img
                                loading="lazy"
                                width="18"
                                height="18"
                                src="/assets/footer/facebook.svg"
                                alt="Facebook"
                            />
                        </a>
                    </li>

                    <li>
                        <a target="_blank" href="https://twitter.com/prestalo_online" rel="noopener noreferrer">
                            <img loading="lazy" width="18" height="18" src="/assets/footer/x.svg" alt="Twitter" />
                        </a>
                    </li>

                    <li>
                        <a target="_blank" href="https://www.instagram.com/prestalo_online/" rel="noopener noreferrer">
                            <img
                                loading="lazy"
                                width="18"
                                height="18"
                                src="/assets/footer/instagram.svg"
                                alt="Instagram"
                            />
                        </a>
                    </li>

                    <li>
                        <a target="_blank" href="https://www.tiktok.com/@prestalo.com" rel="noopener noreferrer">
                            <img loading="lazy" width="18" height="18" src="/assets/footer/tiktok.svg" alt="Tiktok" />
                        </a>
                    </li>
                </ul>

                {footer.legalCopy && (
                    <Html className="c-white container max-lg mt-32">
                        {footer.legalCopy.html.replace(/\[year\]/, year.toString())}
                    </Html>
                )}
            </div>
        </footer>
    );
};
